import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Video, Building, Clock, Check, Gift, CreditCard, ExternalLink } from 'lucide-react';
import { format, addDays, parseISO, getDay } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();

  const [practitioner, setPractitioner] = useState(null);
  const [slots, setSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [consultationType, setConsultationType] = useState('');
  const [notes, setNotes] = useState('');
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoValidated, setPromoValidated] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);
  
  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState('stripe'); // 'stripe' or 'paypal'

  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/auth?redirect=/booking/${id}`);
      return;
    }

    const fetchData = async () => {
      try {
        const [practResponse, slotsResponse] = await Promise.all([
          axios.get(`${API}/practitioners/${id}`),
          axios.get(`${API}/slots/practitioner/${id}`)
        ]);
        setPractitioner(practResponse.data);
        setSlots(slotsResponse.data);
        
        if (practResponse.data.consultation_types?.length === 1) {
          setConsultationType(practResponse.data.consultation_types[0]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchBookedSlots = async () => {
      if (!selectedDate) return;
      
      try {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const response = await axios.get(`${API}/appointments/practitioner/${id}/booked?date=${dateStr}`);
        setBookedSlots(response.data);
      } catch (error) {
        console.error('Error fetching booked slots:', error);
      }
    };
    fetchBookedSlots();
  }, [selectedDate, id]);

  // Validate promo code
  const handleValidatePromo = async () => {
    if (!promoCode.trim()) return;
    
    setValidatingPromo(true);
    try {
      const res = await axios.post(
        `${API}/validate-promo-code`,
        { code: promoCode, practitioner_id: id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.valid) {
        setPromoValidated(res.data);
        toast.success('Code valide ! Consultation gratuite');
      } else {
        setPromoValidated(null);
        toast.error(res.data.message || 'Code invalide');
      }
    } catch (error) {
      setPromoValidated(null);
      toast.error('Code invalide ou expiré');
    } finally {
      setValidatingPromo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime || !consultationType) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    // Si code promo valide, pas besoin de vérifier le prix
    if (!promoValidated) {
      if (!practitioner?.price || practitioner.price <= 0) {
        toast.error('Ce praticien n\'a pas encore défini son tarif');
        return;
      }
    }

    setSubmitting(true);
    try {
      // Créer le rendez-vous
      const appointmentResponse = await axios.post(
        `${API}/appointments`,
        {
          practitioner_id: id,
          date: format(selectedDate, 'yyyy-MM-dd'),
          start_time: selectedTime,
          consultation_type: consultationType,
          notes: notes || null,
          promo_code: promoValidated ? promoCode.toUpperCase() : null,
          payment_method: paymentMethod // 'stripe' or 'paypal'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const appointmentId = appointmentResponse.data.id;
      
      // Si code promo valide → RDV confirmé directement, pas de paiement
      if (promoValidated) {
        setBookingSuccess(true);
        toast.success('Rendez-vous confirmé (gratuit avec code promo) !');
        return;
      }
      
      // Si PayPal est sélectionné → ouvrir PayPal et montrer succès
      if (paymentMethod === 'paypal' && practitioner?.paypal_email) {
        // Créer le lien PayPal avec le montant
        const paypalEmail = practitioner.paypal_email;
        const amount = practitioner.price;
        const description = encodeURIComponent(`Consultation ${practitioner.user?.first_name} ${practitioner.user?.last_name} - ${format(selectedDate, 'd/MM/yyyy')} ${selectedTime}`);
        
        // PayPal.me link avec montant
        const paypalUrl = `https://www.paypal.com/paypalme/${paypalEmail}/${amount}EUR`;
        
        // Ouvrir PayPal dans un nouvel onglet
        window.open(paypalUrl, '_blank');
        
        // Afficher le succès avec instructions
        setBookingSuccess(true);
        toast.success('Rendez-vous créé ! Veuillez finaliser le paiement sur PayPal.');
        return;
      }
      
      // Sinon, créer la session de paiement Stripe
      try {
        const paymentResponse = await axios.post(
          `${API}/payments/create-checkout`,
          {
            appointment_id: appointmentId,
            origin_url: window.location.origin
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Rediriger vers Stripe Checkout
        if (paymentResponse.data.checkout_url) {
          window.location.href = paymentResponse.data.checkout_url;
        } else {
          throw new Error('URL de paiement non reçue');
        }
      } catch (paymentError) {
        console.error('Payment error:', paymentError);
        toast.error(paymentError.response?.data?.detail || 'Erreur lors de la création du paiement. Veuillez réessayer.');
        setSubmitting(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réservation');
      setSubmitting(false);
    }
  };

  // Get available time slots for selected date
  const getAvailableTimeSlots = () => {
    if (!selectedDate || !slots.length) return [];
    
    const dayOfWeek = getDay(selectedDate);
    const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert Sunday=0 to Monday=0
    
    const daySlots = slots.filter(s => s.day_of_week === adjustedDay);
    const bookedTimes = bookedSlots.map(b => b.start_time);
    
    // Generate time slots from practitioner's availability
    const available = [];
    daySlots.forEach(slot => {
      const [startHour, startMin] = slot.start_time.split(':').map(Number);
      const [endHour, endMin] = slot.end_time.split(':').map(Number);
      const duration = practitioner?.consultation_duration || 60;
      
      let currentHour = startHour;
      let currentMin = startMin;
      
      while (currentHour * 60 + currentMin + duration <= endHour * 60 + endMin) {
        const timeStr = `${String(currentHour).padStart(2, '0')}:${String(currentMin).padStart(2, '0')}`;
        if (!bookedTimes.includes(timeStr)) {
          available.push(timeStr);
        }
        
        currentMin += duration;
        if (currentMin >= 60) {
          currentHour += Math.floor(currentMin / 60);
          currentMin = currentMin % 60;
        }
      }
    });
    
    return [...new Set(available)].sort();
  };

  // Disable dates without slots
  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (date < today) return true;
    if (date > addDays(today, 60)) return true;
    
    const dayOfWeek = getDay(date);
    const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    
    return !slots.some(s => s.day_of_week === adjustedDay);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="loader" />
        </div>
      </div>
    );
  }

  if (bookingSuccess) {
    const isPayPalPayment = paymentMethod === 'paypal' && !promoValidated;
    
    return (
      <div className="min-h-screen bg-background" data-testid="booking-success">
        <Navbar />
        <div className="container-app py-20">
          <div className="max-w-md mx-auto text-center">
            <div className={`w-20 h-20 mx-auto mb-6 ${isPayPalPayment ? 'bg-yellow-100' : 'bg-green-100'} rounded-full flex items-center justify-center`}>
              <Check className={`w-10 h-10 ${isPayPalPayment ? 'text-yellow-600' : 'text-green-600'}`} />
            </div>
            <h1 className="font-heading text-2xl font-semibold mb-4">
              {isPayPalPayment ? 'Rendez-vous créé !' : 'Rendez-vous confirmé !'}
            </h1>
            <p className="text-muted-foreground mb-4">
              Votre rendez-vous avec {practitioner?.user?.first_name} {practitioner?.user?.last_name} 
              {selectedDate && ` le ${format(selectedDate, 'EEEE d MMMM yyyy', { locale: fr })}`} à {selectedTime} a bien été enregistré.
            </p>
            
            {isPayPalPayment && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6 text-left">
                <p className="text-sm text-yellow-800 font-medium mb-2">
                  Important : Finalisez votre paiement
                </p>
                <p className="text-sm text-yellow-700">
                  Une fenêtre PayPal s'est ouverte pour effectuer le paiement. 
                  Le praticien confirmera votre rendez-vous une fois le paiement reçu.
                </p>
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/client/dashboard">
                <Button className="bg-sage-500 hover:bg-sage-600" data-testid="view-appointments-btn">
                  Voir mes rendez-vous
                </Button>
              </Link>
              <Link to="/search">
                <Button variant="outline" data-testid="search-more-btn">
                  Rechercher d'autres praticiens
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const availableTimeSlots = getAvailableTimeSlots();

  return (
    <div className="min-h-screen bg-background" data-testid="booking-page">
      <Navbar />
      
      <div className="container-app py-8">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour au profil
        </button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Booking Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-sand-200 shadow-soft p-6 md:p-8">
              <h1 className="font-heading text-2xl font-semibold mb-6">
                Réserver avec {practitioner?.user?.first_name} {practitioner?.user?.last_name}
              </h1>

              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Date Selection */}
                <div>
                  <h2 className="font-medium text-lg mb-4">1. Choisir une date</h2>
                  <div className="flex justify-center">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        setSelectedDate(date);
                        setSelectedTime('');
                      }}
                      disabled={isDateDisabled}
                      locale={fr}
                      className="rounded-xl border border-sand-200"
                      data-testid="booking-calendar"
                    />
                  </div>
                </div>

                {/* Time Selection */}
                {selectedDate && (
                  <div className="animate-fadeIn" data-testid="time-selection">
                    <h2 className="font-medium text-lg mb-4">2. Choisir un horaire</h2>
                    {availableTimeSlots.length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">
                        Aucun créneau disponible pour cette date
                      </p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                        {availableTimeSlots.map((time) => (
                          <button
                            key={time}
                            type="button"
                            onClick={() => setSelectedTime(time)}
                            className={`time-slot ${selectedTime === time ? 'time-slot-selected' : ''}`}
                            data-testid={`time-slot-${time}`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Consultation Type */}
                {selectedTime && practitioner?.consultation_types?.length > 1 && (
                  <div className="animate-fadeIn" data-testid="consultation-type-selection">
                    <h2 className="font-medium text-lg mb-4">3. Type de consultation</h2>
                    <RadioGroup value={consultationType} onValueChange={setConsultationType}>
                      <div className="grid sm:grid-cols-2 gap-4">
                        {practitioner.consultation_types.includes('cabinet') && (
                          <Label
                            htmlFor="cabinet"
                            className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                              consultationType === 'cabinet'
                                ? 'border-sage-500 bg-sage-50'
                                : 'border-sand-200 hover:border-sand-300'
                            }`}
                          >
                            <RadioGroupItem value="cabinet" id="cabinet" />
                            <Building className="w-5 h-5 text-sage-600" />
                            <div>
                              <p className="font-medium">En cabinet</p>
                              <p className="text-xs text-muted-foreground">Sur place</p>
                            </div>
                          </Label>
                        )}
                        {practitioner.consultation_types.includes('visio') && (
                          <Label
                            htmlFor="visio"
                            className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                              consultationType === 'visio'
                                ? 'border-terracotta-500 bg-terracotta-50'
                                : 'border-sand-200 hover:border-sand-300'
                            }`}
                          >
                            <RadioGroupItem value="visio" id="visio" />
                            <Video className="w-5 h-5 text-terracotta-500" />
                            <div>
                              <p className="font-medium">En visio</p>
                              <p className="text-xs text-muted-foreground">À distance</p>
                            </div>
                          </Label>
                        )}
                      </div>
                    </RadioGroup>
                  </div>
                )}

                {/* Notes */}
                {(selectedTime && (consultationType || practitioner?.consultation_types?.length === 1)) && (
                  <div className="animate-fadeIn" data-testid="notes-section">
                    <h2 className="font-medium text-lg mb-4">
                      {practitioner?.consultation_types?.length > 1 ? '4.' : '3.'} Notes (optionnel)
                    </h2>
                    <Textarea
                      placeholder="Décrivez brièvement la raison de votre consultation..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="form-textarea min-h-[100px]"
                      data-testid="booking-notes"
                    />
                  </div>
                )}

                {/* Promo Code (Hypocagne) */}
                {(selectedTime && (consultationType || practitioner?.consultation_types?.length === 1)) && (
                  <div className="animate-fadeIn" data-testid="promo-code-section">
                    <h2 className="font-medium text-lg mb-4 flex items-center gap-2">
                      <Gift className="w-5 h-5 text-purple-500" />
                      Code promo (optionnel)
                    </h2>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Entrez votre code"
                        value={promoCode}
                        onChange={(e) => { setPromoCode(e.target.value); setPromoValidated(null); }}
                        className="flex-1"
                        disabled={promoValidated}
                      />
                      {!promoValidated ? (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleValidatePromo}
                          disabled={!promoCode.trim() || validatingPromo}
                        >
                          {validatingPromo ? '...' : 'Valider'}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => { setPromoCode(''); setPromoValidated(null); }}
                          className="text-red-500"
                        >
                          Annuler
                        </Button>
                      )}
                    </div>
                    {promoValidated && (
                      <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        <span>Code valide - Consultation gratuite !</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Payment Method Selection (only if PayPal is available and no promo code) */}
                {(selectedTime && (consultationType || practitioner?.consultation_types?.length === 1) && !promoValidated && practitioner?.paypal_email) && (
                  <div className="animate-fadeIn" data-testid="payment-method-section">
                    <h2 className="font-medium text-lg mb-4 flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-blue-500" />
                      Moyen de paiement
                    </h2>
                    <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                      <div className="grid sm:grid-cols-2 gap-4">
                        {/* Stripe option */}
                        <Label
                          htmlFor="payment-stripe"
                          className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            paymentMethod === 'stripe'
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-sand-200 hover:border-sand-300'
                          }`}
                        >
                          <RadioGroupItem value="stripe" id="payment-stripe" />
                          <div className="flex-1">
                            <p className="font-medium">Carte bancaire</p>
                            <p className="text-xs text-muted-foreground">Paiement sécurisé via Stripe</p>
                          </div>
                          <CreditCard className="w-5 h-5 text-blue-500" />
                        </Label>
                        
                        {/* PayPal option */}
                        <Label
                          htmlFor="payment-paypal"
                          className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            paymentMethod === 'paypal'
                              ? 'border-yellow-500 bg-yellow-50'
                              : 'border-sand-200 hover:border-sand-300'
                          }`}
                        >
                          <RadioGroupItem value="paypal" id="payment-paypal" />
                          <div className="flex-1">
                            <p className="font-medium">PayPal</p>
                            <p className="text-xs text-muted-foreground">Paiement direct au praticien</p>
                          </div>
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#003087">
                            <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.629h6.304c2.091 0 3.787.581 4.907 1.682 1.049 1.031 1.48 2.448 1.282 4.214-.053.465-.145.91-.273 1.332-.91 3.064-3.286 4.675-6.868 4.675H9.097a.77.77 0 0 0-.757.629l-.98 5.47a.641.641 0 0 1-.633.54l.35-.296zm8.89-13.276c.093-.54.11-1.019.045-1.432-.28-1.8-1.86-2.568-4.048-2.568H7.117l-2.06 13.125h3.134l.769-4.876a.77.77 0 0 1 .757-.629h1.955c2.862 0 5.039-1.31 5.687-3.91.104-.42.169-.824.193-1.21.018-.168.027-.334.027-.5h.387z"/>
                          </svg>
                        </Label>
                      </div>
                    </RadioGroup>
                    
                    {paymentMethod === 'paypal' && (
                      <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                        <p className="flex items-center gap-2">
                          <ExternalLink className="w-4 h-4" />
                          Vous serez redirigé vers PayPal pour effectuer le paiement directement au praticien.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Submit */}
                {selectedDate && selectedTime && (consultationType || practitioner?.consultation_types?.length === 1) && (
                  <Button
                    type="submit"
                    disabled={submitting}
                    className={`w-full h-12 ${paymentMethod === 'paypal' && !promoValidated ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-terracotta-500 hover:bg-terracotta-600'} text-white rounded-xl font-medium animate-fadeIn`}
                    data-testid="submit-booking"
                  >
                    {submitting 
                      ? (promoValidated ? 'Confirmation...' : paymentMethod === 'paypal' ? 'Ouverture de PayPal...' : 'Redirection vers le paiement...') 
                      : (promoValidated 
                          ? 'Confirmer (gratuit)' 
                          : paymentMethod === 'paypal' 
                            ? `Payer ${practitioner?.price || ''}€ via PayPal`
                            : `Payer ${practitioner?.price || ''}€ par carte`)
                    }
                  </Button>
                )}
              </form>
            </div>
          </div>

          {/* Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-sand-200 shadow-soft p-6 sticky top-24" data-testid="booking-summary">
              <h2 className="font-heading text-lg font-medium mb-4">Récapitulatif</h2>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-sage-100 rounded-xl flex items-center justify-center">
                    <span className="font-heading font-semibold text-sage-600">
                      {practitioner?.user?.first_name?.[0]}{practitioner?.user?.last_name?.[0]}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium">{practitioner?.user?.first_name} {practitioner?.user?.last_name}</p>
                    <p className="text-sm text-muted-foreground capitalize">{practitioner?.specialty}</p>
                  </div>
                </div>

                <div className="border-t border-sand-200 pt-4 space-y-3">
                  {selectedDate && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Date</span>
                      <span className="font-medium">{format(selectedDate, 'd MMMM yyyy', { locale: fr })}</span>
                    </div>
                  )}
                  {selectedTime && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Heure</span>
                      <span className="font-medium">{selectedTime}</span>
                    </div>
                  )}
                  {consultationType && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Type</span>
                      <span className="font-medium capitalize">
                        {consultationType === 'cabinet' ? 'En cabinet' : 'En visio'}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Durée</span>
                    <span className="font-medium">{practitioner?.consultation_duration} min</span>
                  </div>
                </div>

                {practitioner?.price && (
                  <div className="border-t border-sand-200 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Total</span>
                      <span className="text-xl font-heading font-semibold text-sage-600">
                        {promoValidated ? '0€' : `${practitioner.price}€`}
                      </span>
                    </div>
                    {promoValidated ? (
                      <p className="text-xs text-green-600 mt-1 font-medium">Gratuit avec code promo</p>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-1">
                        {paymentMethod === 'paypal' ? 'Paiement via PayPal' : 'Paiement sécurisé par carte bancaire'}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
